import { useEffect, useRef, useState } from 'react';
import {
  type CameraState,
  FALLBACK_HINTS,
  captureFrame,
  isFallbackState,
  startCamera,
  stopStream,
} from '../lib/cameraCapture';
import { Icon } from './Icon';

/**
 * In-app camera modal (R23 C2).
 *
 * The real camera surface for the Card→Camera option — replaces the file-picker the `capture`
 * attribute silently fell through to on the kiosk device (dev-89 turns 35–48). Opens a `getUserMedia`
 * live preview (rear/environment camera preferred), captures a frame on tap, and hands the resulting
 * File to the SAME downstream OCR/upload flow a picked file uses (`onCapture`). Every non-live state
 * (permission denied / no camera / unsupported / error) shows an honest hint + a file-picker escape
 * (`onUseFilePicker`) so the visitor is never stranded.
 *
 * Design-system-consistent: token surfaces (`bg-elevated`, `ring-line-strong`, brass accents),
 * `.reveal` motion (reduced-motion safe), Icon primitive, kiosk-sized touch targets. Presentation
 * only — all the state-machine + mediaDevices classification + frame encode live in the pure,
 * node-tested `lib/cameraCapture.ts`.
 */

type Props = {
  /** Captured frame → the host's existing scan/upload flow (same path a picked file takes). */
  onCapture: (file: File) => void;
  /** Fall back to the OS file picker (from any non-live state) — the honest escape. */
  onUseFilePicker: () => void;
  /** Close the modal without capturing. */
  onClose: () => void;
};

export function CameraCaptureModal({ onCapture, onUseFilePicker, onClose }: Props) {
  const [state, setState] = useState<CameraState>('requesting');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Open the stream on mount; always stop every track on unmount (kiosk hygiene — never leave the
  // camera light on for the next visitor).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
      const res = await startCamera(md);
      if (cancelled) {
        if (res.ok) stopStream(res.stream);
        return;
      }
      if (res.ok) {
        streamRef.current = res.stream;
        if (videoRef.current) {
          videoRef.current.srcObject = res.stream;
        }
        setState('live');
      } else {
        setState(res.reason);
      }
    })();
    return () => {
      cancelled = true;
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, []);

  async function onCaptureClick() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    setState('capturing');
    const file = await captureFrame(video, canvas);
    if (!file) {
      // Frame not ready / encode failed — drop back to live so the visitor can retry.
      setState('live');
      return;
    }
    stopStream(streamRef.current);
    streamRef.current = null;
    onCapture(file);
  }

  const fallback = isFallbackState(state) ? state : null;

  return (
    <dialog
      open
      className="fixed inset-0 z-[calc(var(--z-overlay)+2)] m-0 grid h-full max-h-none w-full max-w-none place-items-center bg-ink/50 p-4"
      aria-label="Take a photo of your card"
      data-testid="camera-capture-modal"
    >
      <div className="reveal w-full max-w-md rounded-2xl bg-elevated ring-1 ring-line-strong shadow-soft p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="font-display text-sm text-ink">Take a photo of your card</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close camera"
            className="grid h-9 w-9 place-items-center rounded-full text-subtle ring-1 ring-line hover:text-ink hover:ring-line-strong transition-colors"
          >
            <Icon name="x" size={15} />
          </button>
        </div>

        {/* Live preview — kept mounted through requesting/live/capturing so the ref is stable. Hidden
            in fallback states (no stream). Rounded, ringed frame consistent with the design system. */}
        {!fallback && (
          <div className="relative overflow-hidden rounded-xl bg-canvas ring-1 ring-line aspect-[4/3]">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
              data-testid="camera-preview"
            />
            {(state === 'requesting' || state === 'capturing') && (
              <div className="absolute inset-0 grid place-items-center bg-ink/40">
                <span className="text-xs text-canvas">
                  {state === 'requesting' ? 'Opening camera…' : 'Capturing…'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Fallback panel — honest hint + the file-picker escape. */}
        {fallback && (
          <div className="flex flex-col gap-3 rounded-xl bg-fill ring-1 ring-line p-4 text-center">
            <span className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-brass-soft text-brass">
              <Icon name="camera" size={18} />
            </span>
            <p className="text-sm text-subtle leading-relaxed">{FALLBACK_HINTS[fallback]}</p>
          </div>
        )}

        {/* Action row */}
        <div className="flex items-center gap-2">
          {state === 'live' && (
            <button
              type="button"
              onClick={() => void onCaptureClick()}
              className="btn-primary flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm"
              data-testid="camera-capture-btn"
            >
              <Icon name="camera" size={16} />
              Capture
            </button>
          )}
          <button
            type="button"
            onClick={onUseFilePicker}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm text-subtle ring-1 ring-line hover:text-ink hover:ring-line-strong transition-colors"
            data-testid="camera-use-file-btn"
          >
            <Icon name="image" size={16} />
            Pick a photo
          </button>
        </div>

        {/* Off-screen canvas used only to grab the captured frame. */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </dialog>
  );
}
